<template>
  <div class="crew-basic-info-form">
    <div class="flex gap-x-8">
      <div class="w-1/2">
        <TextInput
          label="氏名"
          v-model="localName"
          placeholder="例）海運太郎"
          :readOnly="!isEditMode"
          isRequired
          :errorMessage="validationErrors.name"
        />
      </div>
      <div class="w-1/2">
        <TextInput
          label="氏名（カナ）"
          v-model="localNameKana"
          placeholder="例）カイウンタロウ"
          :readOnly="!isEditMode"
          :errorMessage="validationErrors.nameKana"
        />
      </div>
    </div>
    <div class="flex gap-x-8 mt-6">
      <div class="w-full">
        <AffiliatedCompanyInput
          label="社員区分 / 所属企業"
          v-model="localAffiliatedCompanies"
          :readOnly="!isEditMode"
          isRequired
          :errorMessage="validationErrors.affiliatedCompanies"
        />
      </div>
    </div>
    <div class="flex gap-x-8 mt-6">
      <div class="w-full">
        <AvailableRoleInput
          label="辞令職 / 執職可能役職"
          v-model="localAvailableRoles"
          :roleOptions="roleOptions"
          :readOnly="!isEditMode"
          :errorMessage="validationErrors.availableRoles"
        />
      </div>
    </div>
    <div class="flex gap-x-8 mt-6">
      <div class="w-1/2">
        <DateInput
          label="入社日"
          v-model="localJoiningDate"
          placeholder="---- / -- / --"
          :readOnly="!isEditMode"
          :errorMessage="validationErrors.joiningDate"
        />
      </div>
      <div class="w-1/2">
        <DateInput
          label="退職日"
          v-model="localRetirementDate"
          placeholder="---- / -- / --"
          :readOnly="!isEditMode"
          :errorMessage="validationErrors.retirementDate"
        />
      </div>
    </div>
    <div class="flex gap-x-8 mt-6">
      <div class="w-1/2">
        <TextInput
          label="社員番号"
          v-model="localEmployeeNumber"
          placeholder="例）123456"
          :readOnly="!isEditMode"
          :errorMessage="validationErrors.employeeNumber"
        />
      </div>
      <div class="w-1/2">
        <RadioInput
          label="組合員該当"
          v-model="localUnionMember"
          :options="[
            { label: 'はい', value: true },
            { label: 'いいえ', value: false },
          ]"
          :readOnly="!isEditMode"
          :errorMessage="validationErrors.unionMember"
        />
      </div>
    </div>
    <div class="flex gap-x-8 mt-6">
      <div class="w-1/2">
        <TextInput
          label="メールアドレス"
          v-model="localEmailAddress"
          placeholder="例）kaiun.taro@aideaakaaun.com"
          :readOnly="!isEditMode"
          :errorMessage="validationErrors.emailAddress"
        />
      </div>
      <div class="w-1/2">
        <TextInput
          label="電話番号"
          v-model="localPhoneNumber"
          placeholder="例）09011111111"
          :readOnly="!isEditMode"
          :errorMessage="validationErrors.phoneNumber"
        />
      </div>
    </div>
    <div class="flex gap-x-8 mt-6 mr-8">
      <div class="w-1/2">
        <DateInput
          label="生年月日"
          v-model="localBirthDate"
          placeholder="---- / -- / --"
          :readOnly="!isEditMode"
          :errorMessage="validationErrors.birthDate"
        />
      </div>
    </div>
    <div class="flex gap-x-8 mt-6">
      <div class="w-full">
        <AddressInput
          label="住所"
          prefecturePlaceholder="都道府県"
          :postalCode.sync="localPostalCode"
          :prefecture.sync="localPrefecture"
          :address.sync="localAddress"
          :readOnly="!isEditMode"
          :errorMessage="validationErrors.address"
        />
      </div>
    </div>
    <div class="flex gap-x-8 mt-6">
      <div class="w-full">
        <LongTextInput
          label="備考"
          v-model="localMemo"
          placeholder="例）備考を入力"
          :readOnly="!isEditMode"
          :errorMessage="validationErrors.memo"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TextInput from "../components/form/TextInput.vue";
import LongTextInput from "../components/form/LongTextInput.vue";
import DateInput from "../components/form/DateInput.vue";
import RadioInput from "../components/form/RadioInput.vue";
import AddressInput from "../components/form/AddressInput.vue";
import AffiliatedCompanyInput from "../components/form/AffiliatedCompanyInput.vue";
import AvailableRoleInput from "../components/form/AvailableRoleInput.vue";
import ValidationUtil from "@/utils/entity/ValidationUtil";

export default {
  name: "CrewBasicInfoForm",
  components: {
    TextInput,
    LongTextInput,
    DateInput,
    RadioInput,
    AddressInput,
    AffiliatedCompanyInput,
    AvailableRoleInput,
  },
  props: {
    /**
     * 編集モードかどうか
     * @type {Boolean}
     * @default false
     */
    isEditMode: {
      type: Boolean,
      default: false,
    },
    /**
     * 氏名
     * @type {String}
     * @default ""
     */
    name: {
      type: String,
      default: "",
    },
    /**
     * 氏名(カナ)
     * @type {String}
     * @default ""
     */
    nameKana: {
      type: String,
      default: "",
    },
    /**
     * 所属企業
     * @type {Array}
     * @default []
     */
    affiliatedCompanies: {
      type: Array,
      default: () => [],
    },
    /**
     * 入社日
     * @type {String}
     * @default ""
     */
    joiningDate: {
      type: String,
      default: "",
    },
    /**
     * 退職日
     * @type {String}
     * @default ""
     */
    retirementDate: {
      type: String,
      default: "",
    },
    /**
     * 社員番号
     * @type {String}
     * @default ""
     */
    employeeNumber: {
      type: String,
      default: "",
    },
    /**
     * 組合員該当
     * @type {String}
     * @default ""
     */
    unionMember: {
      type: Boolean,
      default: false,
    },
    /**
     * メールアドレス
     * @type {String}
     * @default ""
     */
    emailAddress: {
      type: String,
      default: "",
    },
    /**
     * 電話番号
     * @type {String}
     * @default ""
     */
    phoneNumber: {
      type: String,
      default: "",
    },
    /**
     * 生年月日
     * @type {String}
     * @default ""
     */
    birthDate: {
      type: String,
      default: "",
    },
    /**
     * 住所
     * @type {String}
     * @default ""
     */
    address: {
      type: String,
      default: "",
    },
    /**
     * 郵便番号
     * @type {String}
     * @default ""
     */
    postalCode: {
      type: String,
      default: "",
    },
    /**
     * 都道府県
     * @type {String}
     * @default ""
     */
    prefecture: {
      type: String,
      default: "",
    },
    /**
     * 備考
     * @type {String}
     * @default ""
     */
    memo: {
      type: String,
      default: "",
    },
    /**
     * 辞令職と執職可能役職
     * @type {Array}
     * @default []
     */
    availableRoles: {
      type: Array,
      default: () => [],
    },
    /**
     * 役職オプション
     * @type {Array}
     * @default []
     */
    roleOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      validationErrors: {},
    };
  },
  computed: {
    localName: {
      get() {
        return this.name;
      },
      set(value) {
        this.$emit("update:name", value);
      },
    },
    localNameKana: {
      get() {
        return this.nameKana;
      },
      set(value) {
        this.$emit("update:nameKana", value);
      },
    },
    localAvailableRoles: {
      get() {
        return this.availableRoles;
      },
      set(value) {
        this.$emit("update:availableRoles", value);
      },
    },
    localAffiliatedCompanies: {
      get() {
        return this.affiliatedCompanies;
      },
      set(value) {
        this.$emit("update:affiliatedCompanies", value);
      },
    },
    localJoiningDate: {
      get() {
        return this.joiningDate;
      },
      set(value) {
        this.$emit("update:joiningDate", value);
      },
    },
    localRetirementDate: {
      get() {
        return this.retirementDate;
      },
      set(value) {
        this.$emit("update:retirementDate", value);
      },
    },
    localEmployeeNumber: {
      get() {
        return this.employeeNumber;
      },
      set(value) {
        this.$emit("update:employeeNumber", value);
      },
    },
    localUnionMember: {
      get() {
        return this.unionMember;
      },
      set(value) {
        this.$emit("update:unionMember", value);
      },
    },
    localEmailAddress: {
      get() {
        return this.emailAddress;
      },
      set(value) {
        this.$emit("update:emailAddress", value);
      },
    },
    localPhoneNumber: {
      get() {
        return this.phoneNumber;
      },
      set(value) {
        this.$emit("update:phoneNumber", value);
      },
    },
    localBirthDate: {
      get() {
        return this.birthDate;
      },
      set(value) {
        this.$emit("update:birthDate", value);
      },
    },
    localPostalCode: {
      get() {
        return this.postalCode;
      },
      set(value) {
        this.$emit("update:postalCode", value);
      },
    },
    localPrefecture: {
      get() {
        return this.prefecture;
      },
      set(value) {
        this.$emit("update:prefecture", value);
      },
    },
    localAddress: {
      get() {
        return this.address;
      },
      set(value) {
        this.$emit("update:address", value);
      },
    },
    localMemo: {
      get() {
        return this.memo;
      },
      set(value) {
        this.$emit("update:memo", value);
      },
    },
  },
  methods: {
    /**
     * バリデーションを実行
     * @returns {boolean} バリデーションが成功したかどうか
     */
    validate() {
      this.validationErrors = {};

      // 氏名のバリデーション（必須）
      if (!this.name || this.name.trim() === "") {
        this.validationErrors.name = "氏名は必須項目です";
      } else if (this.name.length > 50) {
        this.validationErrors.name = "氏名は50文字以内で入力してください";
      }

      // 社員区分 / 所属企業のバリデーション（必須）
      if (
        !this.affiliatedCompanies ||
        this.affiliatedCompanies.length === 0 ||
        this.affiliatedCompanies.every(
          item =>
            (!item.start_date || item.start_date === "") &&
            (!item.status || item.status === "") &&
            (!item.name || item.name.trim() === "")
        )
      ) {
        this.validationErrors.affiliatedCompanies =
          "社員区分 / 所属企業は必須項目です";
      } else {
        // 各項目のバリデーション
        const usedStartDates = new Set();
        for (let i = 0; i < this.affiliatedCompanies.length; i++) {
          const item = this.affiliatedCompanies[i];
          // 空の行はスキップ（すべて空の場合のみ）
          if (
            (!item.start_date || item.start_date === "") &&
            (!item.status || item.status === "") &&
            (!item.name || item.name.trim() === "")
          ) {
            continue;
          }
          if (!item.start_date || item.start_date === "") {
            this.validationErrors.affiliatedCompanies =
              "開始日を入力してください";
            break;
          }
          // 開始日の重複チェック
          if (usedStartDates.has(item.start_date)) {
            this.validationErrors.affiliatedCompanies =
              "開始日が重複しています";
            break;
          }
          usedStartDates.add(item.start_date);
          if (!item.status || item.status === "") {
            this.validationErrors.affiliatedCompanies =
              "社員区分を選択してください";
            break;
          }
          if (!item.name || item.name.trim() === "") {
            this.validationErrors.affiliatedCompanies =
              "所属企業を入力してください";
            break;
          }
        }
      }

      // 氏名（カナ）のバリデーション
      if (this.nameKana && this.nameKana.trim() !== "") {
        const katakanaRegex = /^[ァ-ヶー\s]+$/;
        if (!katakanaRegex.test(this.nameKana)) {
          this.validationErrors.nameKana =
            "氏名（カナ）は全角カタカナで入力してください";
        } else if (this.nameKana.length > 50) {
          this.validationErrors.nameKana =
            "氏名（カナ）は50文字以内で入力してください";
        }
      }

      // 社員番号のバリデーション
      if (this.employeeNumber && this.employeeNumber.trim() !== "") {
        const alphanumericRegex = /^[a-zA-Z0-9]+$/;
        if (!alphanumericRegex.test(this.employeeNumber)) {
          this.validationErrors.employeeNumber =
            "社員番号は英数字で入力してください";
        } else if (this.employeeNumber.length > 10) {
          this.validationErrors.employeeNumber =
            "社員番号は10文字以内で入力してください";
        }
      }

      // 電話番号のバリデーション
      if (this.phoneNumber && this.phoneNumber.trim() !== "") {
        // ハイフンなしの数字のみ許可
        const phoneDigitsRegex = /^\d+$/;
        if (!phoneDigitsRegex.test(this.phoneNumber)) {
          this.validationErrors.phoneNumber =
            "電話番号はハイフンなしの数字のみで入力してください";
        } else {
          // libphonenumber-jsを使用して検証（日本の電話番号）
          if (!ValidationUtil.validateTel_jp(this.phoneNumber)) {
            this.validationErrors.phoneNumber =
              "正しい電話番号形式で入力してください";
          }
        }
      }

      // 辞令職 / 執職可能役職のバリデーション
      if (this.availableRoles && this.availableRoles.length > 0) {
        const usedStartDates = new Set();
        for (let i = 0; i < this.availableRoles.length; i++) {
          const item = this.availableRoles[i];
          // 一つでも入力があれば、全ての項目が必須
          const hasAnyInput =
            (item.start_date && item.start_date !== "") ||
            (item.role_id && item.role_id !== null) ||
            (item.available_role_id && item.available_role_id !== null);

          if (hasAnyInput) {
            if (!item.start_date || item.start_date === "") {
              this.validationErrors.availableRoles = "開始日を入力してください";
              break;
            }
            // 開始日の重複チェック
            if (usedStartDates.has(item.start_date)) {
              this.validationErrors.availableRoles = "開始日が重複しています";
              break;
            }
            usedStartDates.add(item.start_date);
            if (!item.role_id || item.role_id === null) {
              this.validationErrors.availableRoles = "辞令職を選択してください";
              break;
            }
            if (!item.available_role_id || item.available_role_id === null) {
              this.validationErrors.availableRoles =
                "執職可能役職を選択してください";
              break;
            }
          }
        }
      }

      // 郵便番号のバリデーション
      if (this.postalCode && this.postalCode.trim() !== "") {
        const postalCodeRegex = /^\d{7}$/;
        if (!postalCodeRegex.test(this.postalCode)) {
          this.validationErrors.address =
            "郵便番号はハイフンなし数字7桁で入力してください";
        }
      }

      // メールアドレスのバリデーション（形式チェックと文字数制限）
      if (this.emailAddress && this.emailAddress.trim() !== "") {
        const email = this.emailAddress.trim();

        // 全体の文字数チェック（最大256文字）
        if (email.length > 256) {
          this.validationErrors.emailAddress = "メールアドレスが長すぎます";
        } else {
          // 形式チェック
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(email)) {
            this.validationErrors.emailAddress =
              "正しいメールアドレス形式で入力してください";
          } else {
            // ローカルパート（@より前）の文字数チェック（最大64文字）
            const localPart = email.split("@")[0];
            if (localPart && localPart.length > 64) {
              this.validationErrors.emailAddress = "メールアドレスが長すぎます";
            }
          }
        }
      }

      // 退職日のバリデーション（入社日より後であること）
      if (this.joiningDate && this.retirementDate) {
        const joiningDate = new Date(this.joiningDate);
        const retirementDate = new Date(this.retirementDate);
        if (retirementDate < joiningDate) {
          this.validationErrors.retirementDate =
            "退職日は入社日より後の日付を入力してください";
        }
      }

      // エラーがあるかどうかを返す
      return Object.keys(this.validationErrors).length === 0;
    },
    /**
     * エラーをクリア
     */
    clearvalidationErrors() {
      this.validationErrors = {};
    },
    /**
     * エラーをクリア（公開API用ラッパー）
     * CrewBasicInfo.vue などから呼ばれる
     */
    clearErrors() {
      this.clearvalidationErrors();
    },
  },
};
</script>

<style lang="scss" scoped>
// 必要に応じてスタイルを追加
</style>
